import { Component, Inject } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Observable } from "rxjs";
import { AdmRelationship } from "../../api/models/adm-relationship";
import { AdmTable } from "../../api/models/adm-table";
import { AdmTableColumn } from "../../api/models/adm-table-column";
import { DialogResult, DialogResultType } from "../DialogResult";
import { CARDINALITIES, RELATIONSHIP_KINDS, FILTER_SCOPES } from "../../modules/admin/controls/admin-adm-definition/admin-adm-definition.component";

export interface AdmRelationshipDialogData {
  relationship: AdmRelationship;
  tables: AdmTable[];
  physicalColumns: AdmTableColumn[];
  columnLabel: (id: number) => string;
  // Flattened columns whose path walks through this relationship - shown so deleting isn't a surprise, since the
  // server removes them along with it (their value is meaningless without this hop). Computed by the host, not
  // this dialog, since it needs the full column set, not just physicalColumns.
  dependentFlattenedColumns: AdmTableColumn[];
}

// Admin ADM Definition > Relationships > one relationship between two physical columns.
@Component({
  selector: "adm-relationship-dialog",
  templateUrl: "./adm-relationship-dialog.component.html",
})
export class AdmRelationshipDialogComponent {
  public result: AdmRelationship;
  public Result: DialogResult = { ResultType: DialogResultType.Cancel, ResultData: null };
  public cardinalities = CARDINALITIES;
  public relationshipKinds = RELATIONSHIP_KINDS;
  public filterScopes = FILTER_SCOPES;

  // Each side is picked as a table, then one of that table's columns. Only the column id is stored on the
  // relationship; the tables are UI state, derived from the column ids when the dialog opens.
  public fromTableId: number | null = null;
  public toTableId: number | null = null;

  constructor(public dialogRef: MatDialogRef<AdmRelationshipDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: AdmRelationshipDialogData) {
    this.result = { ...data.relationship };
    this.fromTableId = this.tableOfColumn(this.result.fromColumnId);
    this.toTableId = this.tableOfColumn(this.result.toColumnId);
  }

  private tableOfColumn(columnId: number): number | null {
    const column = this.data.physicalColumns.find((c) => c.admTableColumnId === columnId);
    return column ? column.admTableId : null;
  }

  // Tables that have at least one physical column to relate on, alphabetical.
  get tables(): AdmTable[] {
    const withColumns = new Set(this.data.physicalColumns.map((c) => c.admTableId));
    return (this.data.tables || []).filter((t) => withColumns.has(t.admTableId)).sort((a, b) => a.tableName.localeCompare(b.tableName));
  }

  columnsOf(tableId: number | null): AdmTableColumn[] {
    if (tableId === null) return [];
    return this.data.physicalColumns
      .filter((c) => c.admTableId === tableId)
      .sort((a, b) => (a.sortOrder - b.sortOrder) || (a.outputName || a.columnName).localeCompare(b.outputName || b.columnName));
  }

  columnName(c: AdmTableColumn): string {
    return (c.outputName || c.columnName) + (c.retired ? " (retired)" : "");
  }

  // Picking a different table clears that side's column (it belonged to the old table); picking the same
  // table again keeps it.
  onFromTableChange(): void {
    if (this.tableOfColumn(this.result.fromColumnId) !== this.fromTableId) this.result.fromColumnId = 0;
  }

  onToTableChange(): void {
    if (this.tableOfColumn(this.result.toColumnId) !== this.toTableId) this.result.toColumnId = 0;
  }

  get canSave(): boolean {
    return this.result.fromColumnId > 0 && this.result.toColumnId > 0;
  }

  get isNew(): boolean {
    return !this.data.relationship.admRelationshipId;
  }

  // Filter scope only means anything on a one-to-many relationship (see AdmFilterScope); forced back to
  // Restrict when the cardinality changes away from it, so a stale Qualify can't be saved invisibly.
  get isOneToMany(): boolean {
    return this.result.cardinality === 2;
  }

  onCardinalityChange(): void {
    if (!this.isOneToMany) this.result.filterScope = 0;
  }

  columnLabel(id: number): string {
    return this.data.columnLabel(id);
  }

  onSave(): void {
    if (!this.canSave) return;
    this.Result.ResultType = DialogResultType.Ok;
    this.Result.ResultData = this.result;
    this.dialogRef.close(this.Result);
  }

  onToggleRetired(): void {
    this.Result.ResultType = DialogResultType.Delete;
    this.Result.ResultData = this.result;
    this.dialogRef.close(this.Result);
  }

  // Yes = "permanently delete, confirmed" here, distinct from Delete (Retire/Restore) above - this dialog's own
  // contract with admin-adm-definition.component.ts's openRelationshipDialog, not used anywhere else.
  onDelete(): void {
    const deps = this.data.dependentFlattenedColumns;
    const warning = deps.length > 0
      ? `Delete this relationship? It will also delete ${deps.length} flattened column(s) built from it: ${deps.map((c) => c.outputName).join(", ")}.`
      : "Delete this relationship? This cannot be undone.";
    if (!confirm(warning)) return;
    this.Result.ResultType = DialogResultType.Yes;
    this.Result.ResultData = this.result;
    this.dialogRef.close(this.Result);
  }

  onCancelClick(): void {
    this.dialogRef.close(this.Result);
  }

  static showDialog(dialog: MatDialog, data: AdmRelationshipDialogData): Observable<DialogResult> {
    return dialog.open(AdmRelationshipDialogComponent, { disableClose: false, width: "800px", data }).afterClosed();
  }
}
